import type { ConsentRecord, ConsentRequirement } from "./types";

/**
 * Consent bookkeeping.
 *
 * There is no "pending consents" endpoint: the required set comes from a realm's
 * `client-assets` (derived from the pack-dependency closure), the granted set
 * from the subject-scoped `/v1/consent`, and the delta is a client-side set
 * difference. The match key is `(pack_id, purpose)` — the same rule the CLI uses,
 * so the two surfaces cannot disagree about what is outstanding.
 *
 * A consent is `(subject, pack_id, purpose)` and is **not** realm-scoped: two
 * realms binding `kw:personal` raise the same requirement. Hence the union below
 * dedupes and remembers which realms asked, instead of offering a per-realm grant
 * button for a distinction the data model does not make.
 */

/** Composite key; NUL is written explicitly because it cannot occur in either part. */
export function consentKey(r: { pack_id: string; purpose: string }): string {
  return `${r.pack_id}\u0000${r.purpose}`;
}

export interface RequirementWithRealms extends ConsentRequirement {
  /** Realms whose pack closure raises this requirement. */
  realms: string[];
}

export function unionRequirements(
  perRealm: Array<{ realmId: string; reqs: ConsentRequirement[] }>,
): RequirementWithRealms[] {
  const byKey = new Map<string, RequirementWithRealms>();
  for (const { realmId, reqs } of perRealm) {
    for (const req of reqs) {
      const key = consentKey(req);
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.realms.includes(realmId)) existing.realms.push(realmId);
        continue;
      }
      byKey.set(key, { ...req, realms: [realmId] });
    }
  }
  return [...byKey.values()].sort(
    (a, b) => a.pack_id.localeCompare(b.pack_id) || a.purpose.localeCompare(b.purpose),
  );
}

/** `GET /v1/consent` returns active records only; a revoked one satisfies nothing. */
function activeKeys(granted: ConsentRecord[]): Set<string> {
  return new Set(granted.filter((c) => !c.revoked_at).map(consentKey));
}

export function missingConsents<T extends ConsentRequirement>(
  required: T[],
  granted: ConsentRecord[],
): T[] {
  const have = activeKeys(granted);
  return required.filter((r) => !have.has(consentKey(r)));
}

export function isGranted(
  req: ConsentRequirement,
  granted: ConsentRecord[],
): boolean {
  return activeKeys(granted).has(consentKey(req));
}

/**
 * A summary the operator did not write. `cfg.compliance.purpose_summaries` covers
 * only some purposes; the server falls back to a generic sentence. Mark it so
 * nobody mistakes boilerplate for the terms.
 */
export function isGenericSummary(summary: string, purpose: string): boolean {
  return summary.trim() === `Acknowledge der Nutzungsbedingungen (${purpose}).`;
}

/**
 * The shipped default for `compliance.terms_doc_url_base` points at
 * `docs.kontextwerk.localhost` and no deployment overrides it — so on any real
 * instance the terms link is dead. Surface that as an operator task rather than
 * hiding the link.
 */
export function isUnreachableTermsUrl(url: string, origin: string): boolean {
  if (!url) return true;
  // Parsed WITHOUT a base on purpose: a relative or malformed value would
  // otherwise resolve against the admin origin and look like a healthy link,
  // when in fact the operator has not configured a terms document at all.
  const target = parseAbsolute(url);
  if (!target) return true;
  if (!isLoopback(target)) return false;
  const from = parseAbsolute(origin);
  return !from || !isLoopback(from);
}

function parseAbsolute(value: string): URL | null {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed : null;
  } catch {
    return null;
  }
}

function isLoopback(u: URL): boolean {
  return (
    u.hostname === "localhost" || u.hostname.endsWith(".localhost") || u.hostname === "127.0.0.1"
  );
}
